import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle, RefreshCcw, AlertTriangle } from "lucide-react";

const GRADE_TONE = {
    "A": "pos",
    "B": "brand-amber",
    "C": "brand-amber",
    "D": "neg",
};

const HUMAN_LABELS = {
    blank_contract_value: "Blank / Zero Contract Values",
    negative_work_todo: "Negative Work-to-be-Done",
    orphaned_projects: "Orphaned Projects (missing client)",
    orphaned_contracts: "Orphaned Contracts (missing project)",
    name_variance_suspects: "Name Variance / Possible Duplicates",
    missing_client_master_id: "Missing AWC-CL Master ID",
    missing_pan_gstin: "Missing PAN / GSTIN",
    zero_completion_with_billing: "Billed but 0% Complete",
};

export default function DataQualityReport() {
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState({});

    const load = () => {
        api.get("/data-quality/report")
            .then(r => setData(r.data))
            .catch(e => toast.error("Load failed: " + e.message));
    };

    useEffect(() => { load(); }, []);

    if (!data) return <div className="p-4 text-[var(--text-secondary)] font-mono-fin uppercase text-xs">Loading Data Quality Report…</div>;

    const grade = data.grade;
    const gradeTone = GRADE_TONE[grade] || "brand-amber";

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// SECTION 28 · DATA QUALITY</div>
                    <h1 className="font-display font-black text-3xl flex items-center gap-3">
                        <ShieldAlert className="text-[var(--brand-amber)]" size={26} />
                        Data Quality Report
                    </h1>
                    <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                        Scanned {data.scanned.clients} clients · {data.scanned.projects} projects · {data.scanned.contracts} contracts
                    </p>
                </div>
                <button
                    data-testid="refresh-dq"
                    onClick={load}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] flex items-center gap-1.5"
                >
                    <RefreshCcw size={12} /> Refresh
                </button>
            </div>

            {/* Grade card */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Panel testId="dq-grade" className="md:col-span-1">
                    <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Overall Grade</div>
                    <div className={`text-6xl font-display font-black text-[var(--${gradeTone})] mt-2`}>{grade}</div>
                    <div className="text-xs text-[var(--text-primary)]/60 mt-1">
                        {grade === "A" ? "Excellent — data is clean" : grade === "B" ? "Good — minor issues" : grade === "C" ? "Warning — clean up needed" : "Poor — significant issues"}
                    </div>
                </Panel>
                <Panel testId="dq-total-issues" className="md:col-span-3">
                    <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Total Issues</div>
                    <div className={`text-6xl font-display font-black mt-2 ${data.total_issues === 0 ? "text-[var(--pos)]" : "text-[var(--brand-amber)]"}`}>{data.total_issues}</div>
                    <div className="grid grid-cols-4 gap-2 mt-3">
                        {Object.entries(data.counts).map(([k, v]) => v > 0 && (
                            <div key={k} className="border border-panel px-2 py-1.5">
                                <div className="text-[9px] font-mono-fin uppercase text-[var(--text-secondary)]">{HUMAN_LABELS[k] || k}</div>
                                <div className="text-lg font-mono-fin font-bold text-[var(--neg)]">{v}</div>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>

            {/* Detail cards */}
            {Object.entries(data.findings).map(([category, items]) => items.length > 0 && (
                <Panel
                    key={category}
                    testId={`finding-${category}`}
                    title={`${HUMAN_LABELS[category] || category} · ${items.length}`}
                    subtitle="Click a row to see details"
                >
                    <div className="max-h-[320px] overflow-y-auto">
                        <table className="w-full text-xs font-body">
                            <tbody>
                                {items.map((it, i) => (
                                    <tr key={i} className="border-b border-panel/60 hover:bg-panel/40">
                                        <td className="py-1.5 px-2 w-8 text-[var(--brand-amber)]">
                                            <AlertTriangle size={12} />
                                        </td>
                                        <td className="py-1.5 px-2 font-mono-fin text-[10px] text-[var(--text-primary)]/60">
                                            {it.project_id || it.id_1 || it.contract_id || it.id || ""}
                                        </td>
                                        <td className="py-1.5 px-2 text-[var(--text-primary)]/90 text-[11px]">
                                            {it.name || it.name_1 || ""}
                                            {it.similarity_pct !== undefined && (
                                                <span className="ml-2 text-[var(--brand-amber)]">
                                                    ↔ {it.name_2} ({it.similarity_pct}% similar)
                                                </span>
                                            )}
                                            {it.orphan_client_id && (
                                                <span className="ml-2 text-[var(--neg)]">→ missing client {it.orphan_client_id}</span>
                                            )}
                                            {it.orphan_project_id && (
                                                <span className="ml-2 text-[var(--neg)]">→ missing project {it.orphan_project_id}</span>
                                            )}
                                            {it.work_todo !== undefined && (
                                                <span className="ml-2 text-[var(--neg)]">= ₹{it.work_todo.toLocaleString("en-IN")}</span>
                                            )}
                                            {it.billed !== undefined && (
                                                <span className="ml-2 text-[var(--text-primary)]/60">Billed ₹{it.billed.toLocaleString("en-IN")} · Complete {it.completion_pct}%</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            ))}

            {data.total_issues === 0 && (
                <Panel testId="all-clean" title="No Data Quality Issues Detected">
                    <div className="flex items-center gap-3 text-[var(--pos)]">
                        <CheckCircle size={20} />
                        <div>
                            <div className="text-[var(--text-primary)] font-mono-fin uppercase tracking-wider text-sm">Your data is production-ready</div>
                            <div className="text-[var(--text-secondary)] text-xs mt-1">
                                All {data.scanned.clients} clients, {data.scanned.projects} projects, and {data.scanned.contracts} contracts pass the Section-28 quality checks.
                            </div>
                        </div>
                    </div>
                </Panel>
            )}
        </div>
    );
}
